export const DEFAULT_FRAME_FIT = Object.freeze({ inset: 12, offsetX: 0, offsetY: 0 });

const bounded = (value, fallback, min, max) => {
  if (value === null || value === undefined || value === '') return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};

export function normalizeFrameFit(fit) {
  return {
    inset: bounded(fit?.inset, DEFAULT_FRAME_FIT.inset, 0, 30),
    offsetX: bounded(fit?.offsetX, 0, -15, 15),
    offsetY: bounded(fit?.offsetY, 0, -15, 15),
  };
}

// Physical left/top coordinates intentionally remain identical in RTL and LTR.
// Fit is stored as percentages of the artwork canvas, never CSS pixels.
// Preserve the same opening-to-photo ratio in thumbnails, posts and profiles.
export function framePhotoStyle(fit, radius = '50%') {
  const { inset, offsetX, offsetY } = normalizeFrameFit(fit);
  return {
    position: 'absolute', inset: 'auto',
    left: `${inset + offsetX}%`, top: `${inset + offsetY}%`,
    width: `${100 - 2 * inset}%`, height: `${100 - 2 * inset}%`,
    borderRadius: radius, transform: 'none', boxSizing: 'border-box',
  };
}

// A list can reserve one slot for everyone and size the actual portraits equally.
// Include offsets when checking how much room the artwork needs around that portrait.
export function maxPortraitSize(fit, slotSize) {
  const { inset, offsetX, offsetY } = normalizeFrameFit(fit);
  return slotSize * (1 - inset / 50) / (1 + Math.max(Math.abs(offsetX), Math.abs(offsetY)) / 50);
}

export function frameArtworkStyle(fit, portraitSize) {
  const { inset, offsetX, offsetY } = normalizeFrameFit(fit);
  const canvas = portraitSize / (1 - inset / 50);
  return {
    left: `calc(50% - ${canvas * (0.5 + offsetX / 100)}px)`,
    top: `calc(50% - ${canvas * (0.5 + offsetY / 100)}px)`,
    width: canvas, height: canvas,
  };
}

// Responsive version of the same geometry: the portrait keeps a 1px gutter,
// whether or not it has a frame. Scale the artwork around it, not the photo.
export function responsiveFrameArtworkStyle(fit) {
  const { inset, offsetX, offsetY } = normalizeFrameFit(fit);
  const scale = 1 / (1 - inset / 50);
  const position = offset => `calc(${50 - 100 * scale * (0.5 + offset / 100)}% + ${2 * scale * (0.5 + offset / 100)}px)`;
  return {
    left: position(offsetX), top: position(offsetY),
    width: `calc(${100 * scale}% - ${2 * scale}px)`,
    height: `calc(${100 * scale}% - ${2 * scale}px)`,
  };
}
